import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Owns all data + side-effects for the Video Detail screen:
 * loading the video, fetching voices, generating voiceover/images, delete.
 */
export function useVideoDetail(id) {
  const navigate = useNavigate();
  const { updateCredits } = useAuth();
  const [video, setVideo] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState("");
  const [voiceLoading, setVoiceLoading] = useState(false);
  const [imgLoading, setImgLoading] = useState(null); // active scene index
  const [allImgLoading, setAllImgLoading] = useState(false);
  const [animLoading, setAnimLoading] = useState(null); // scene index being animated
  const [animAllLoading, setAnimAllLoading] = useState(false);
  const [quote, setQuote] = useState(null);

  const fetchQuote = useCallback(() => {
    api.get(`/videos/${id}/motion-quote`).then((r) => setQuote(r.data)).catch(() => {});
  }, [id]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/videos/${id}`);
      setVideo(data);
      if (data.voice_id) setVoiceId(data.voice_id);
    } catch (err) {
      toast.error(apiError(err));
      navigate("/library");
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
    fetchQuote();
    api
      .get("/voices")
      .then((r) => {
        const list = r.data.voices || [];
        setVoices(list);
        setVoiceId((cur) => (list.some((v) => v.voice_id === cur) ? cur : list[0]?.voice_id || ""));
      })
      .catch(() => {});
  }, [load, fetchQuote]);

  const applySceneImage = (idx, data) =>
    setVideo((v) => {
      const scenes = [...v.scenes];
      scenes[idx] = { ...scenes[idx], has_image: true, sid: data.sid, image_updated_at: data.image_updated_at };
      return { ...v, scenes };
    });

  const makeVoiceover = async () => {
    setVoiceLoading(true);
    try {
      const { data } = await api.post(`/videos/${id}/voiceover`, { voice_id: voiceId });
      updateCredits(data.credits);
      toast.success("Voiceover generated!");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setVoiceLoading(false);
    }
  };

  const makeImage = async (idx) => {
    setImgLoading(idx);
    try {
      const { data } = await api.post(`/videos/${id}/scenes/${idx}/image`);
      updateCredits(data.credits);
      applySceneImage(idx, data);
      fetchQuote();
      toast.success(`Scene ${idx + 1} visual ready`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setImgLoading(null);
    }
  };

  const makeAllImages = async (force = false) => {
    setAllImgLoading(true);
    for (let i = 0; i < video.scenes.length; i++) {
      if (!force && video.scenes[i].has_image) continue;
      try {
        const { data } = await api.post(`/videos/${id}/scenes/${i}/image`);
        updateCredits(data.credits);
        applySceneImage(i, data);
      } catch (err) {
        toast.error(apiError(err));
        break;
      }
    }
    setAllImgLoading(false);
    fetchQuote();
    toast.success(force ? "Restyled all scenes" : "Visuals complete");
  };

  // Premium: still image -> true AI motion clip. Generation runs in the background; poll until done.
  const animateScene = async (idx) => {
    setAnimLoading(idx);
    try {
      const { data } = await api.post(`/videos/${id}/scenes/${idx}/animate`);
      updateCredits(data.credits);
      toast.message(`Animating scene ${idx + 1} — this usually takes 1–3 minutes.`);
      const sid = video?.scenes?.[idx]?.sid;
      const deadline = Date.now() + 6 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data: v } = await api.get(`/videos/${id}`);
        const sc = (v.scenes || []).find((s) => s.sid === sid);
        if (sc?.has_clip && sc?.clip_status === "ready") {
          setVideo(v);
          fetchQuote();
          toast.success(`Scene ${idx + 1} is now a motion clip!`);
          return;
        }
        if (sc?.clip_status === "failed") {
          setVideo(v);
          fetchQuote();
          toast.error(sc.clip_error || "Animation failed — credits refunded.");
          return;
        }
      }
      toast.message("Still animating in the background — check back in a minute.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAnimLoading(null);
    }
  };

  // Batch: animate every still scene at once; poll until all clips settle.
  const animateAllScenes = async () => {
    setAnimAllLoading(true);
    try {
      const { data } = await api.post(`/videos/${id}/animate-all`);
      updateCredits(data.credits);
      toast.message(`Animating ${data.animating} scene${data.animating > 1 ? "s" : ""} — this usually takes a few minutes.`);
      const deadline = Date.now() + 15 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 6000));
        const { data: v } = await api.get(`/videos/${id}`);
        setVideo(v);
        const still = (v.scenes || []).some((s) => s.clip_status === "generating");
        if (!still) {
          fetchQuote();
          const failed = (v.scenes || []).filter((s) => s.clip_status === "failed").length;
          if (failed) toast.error(`${failed} scene${failed > 1 ? "s" : ""} failed — those credits were refunded.`);
          else toast.success("All scenes are now motion clips!");
          return;
        }
      }
      toast.message("Still animating in the background — check back in a minute.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setAnimAllLoading(false);
    }
  };

  const remove = async () => {    await api.delete(`/videos/${id}`);
    toast.success("Deleted");
    navigate("/library");
  };

  // ---- Simple scene editor ----
  const updateSceneLocal = (idx, patch) =>
    setVideo((v) => {
      const scenes = [...v.scenes];
      scenes[idx] = { ...scenes[idx], ...patch };
      return { ...v, scenes };
    });

  const saveScene = async (idx, patch) => {
    updateSceneLocal(idx, patch);
    try {
      await api.put(`/videos/${id}/scenes/${idx}`, patch);
    } catch (err) {
      toast.error(apiError(err));
      load();
    }
  };

  const moveScene = async (idx, direction) => {
    const target = direction === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= (video?.scenes?.length || 0)) return;
    setVideo((v) => {
      const scenes = [...v.scenes];
      [scenes[idx], scenes[target]] = [scenes[target], scenes[idx]];
      return { ...v, scenes };
    });
    try {
      await api.post(`/videos/${id}/scenes/${idx}/move`, { direction });
    } catch (err) {
      toast.error(apiError(err));
      load();
    }
  };

  const deleteScene = async (idx) => {
    setVideo((v) => ({ ...v, scenes: v.scenes.filter((_, i) => i !== idx) }));
    try {
      await api.delete(`/videos/${id}/scenes/${idx}`);
    } catch (err) {
      toast.error(apiError(err));
      load();
    }
  };

  return {
    video,
    reload: load,
    quote,
    voices,
    voiceId,
    setVoiceId,
    voiceLoading,
    imgLoading,
    allImgLoading,
    animLoading,
    animAllLoading,
    makeVoiceover,
    makeImage,
    makeAllImages,
    animateScene,
    animateAllScenes,
    remove,
    updateSceneLocal,
    saveScene,
    moveScene,
    deleteScene,
  };
}
